import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  FlaskConical, Calculator, Lock, Flame, Trophy, Crown, ChevronRight,
  BookOpen, Brain, Headphones, ClipboardCheck, ArrowLeft, Upload, Timer,
  Atom,
} from "lucide-react";
import { motion } from "framer-motion";
import { listCurriculumSubjects, listTopics } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import PastDueBanner from "@/components/PastDueBanner";

const ICONS = { FlaskConical, Calculator, BookOpen, Atom };
const COLOR_BG = {
  mint:    "bg-[#9DFFB0] dark:bg-[#68D391]",
  blue:    "bg-[#90E0FF] dark:bg-[#63B3ED]",
  yellow:  "bg-[#FFE066] dark:bg-[#F6E05E]",
  peach:   "bg-[#FFB38E] dark:bg-[#F6AD55]",
  primary: "bg-[#D4C4FB] dark:bg-[#B794F4]",
};

export default function Curriculum() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [subjects, setSubjects] = useState([]);
  const [selected, setSelected] = useState(null); // subject name
  const [topics, setTopics] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    listCurriculumSubjects().then(setSubjects).finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (!selected) return;
    setLoading(true);
    listTopics(selected).then(setTopics).finally(() => setLoading(false));
  }, [selected]);

  const activeChildId = user?.active_child_id;

  return (
    <div className="min-h-screen bg-[#FAFAFA] dark:bg-zinc-950">
      <PastDueBanner />
      {/* Top bar */}
      <header className="nb-border border-l-0 border-r-0 border-t-0 bg-white dark:bg-zinc-900 sticky top-0 z-30">
        <div className="max-w-6xl mx-auto px-6 py-3 flex items-center justify-between gap-3">
          <button onClick={() => navigate("/family")} className="nb-border rounded-xl px-3 py-2 font-bold flex items-center gap-2 text-sm hover:nb-shadow">
            <ArrowLeft className="w-4 h-4" /> Switch profile
          </button>
          <div className="flex items-center gap-2">
            {["active", "trialing"].includes(user?.subscription_status) ? (
              <div className="nb-border rounded-full px-3 py-1.5 bg-[#FFE066] flex items-center gap-1 text-xs font-bold text-black">
                <Crown className="w-3.5 h-3.5" /> {user?.subscription_status === "trialing" ? "Trial" : "Premium"}
              </div>
            ) : (
              <button
                data-testid="upgrade-btn"
                onClick={() => navigate("/pricing")}
                className="nb-button px-3 py-1.5 bg-[#FFE066] dark:bg-[#F6E05E] text-xs flex items-center gap-1"
              >
                <Crown className="w-3.5 h-3.5" /> Upgrade
              </button>
            )}
            <button
              data-testid="personal-subjects-link"
              onClick={() => navigate("/subjects")}
              className="nb-border rounded-xl px-3 py-1.5 text-xs font-bold flex items-center gap-1 hover:nb-shadow"
            >
              <Upload className="w-3.5 h-3.5" /> My notes
            </button>
            <button
              data-testid="leaderboard-link"
              onClick={() => navigate("/leaderboard")}
              className="nb-border rounded-xl px-3 py-1.5 text-xs font-bold flex items-center gap-1 hover:nb-shadow"
            >
              <Trophy className="w-3.5 h-3.5" /> Leaderboard
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-6xl mx-auto p-6 sm:p-10">
        {!selected ? (
          <>
            <div className="mb-8">
              <div className="label-caps mb-2" data-testid="curriculum-board-label">
                {subjects[0]?.exam_board || "GCSE"}
              </div>
              <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter">
                Pick a{" "}
                <span className="bg-[#FFE066] dark:bg-[#F6E05E] px-3 nb-border rounded-2xl inline-block text-black">
                  subject
                </span>
              </h1>
            </div>
            {loading ? (
              <div className="font-bold text-zinc-500">Loading…</div>
            ) : (
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {subjects.map((s, i) => {
                  const Icon = ICONS[s.icon] || BookOpen;
                  return (
                    <motion.button
                      key={s.subject}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: i * 0.06 }}
                      onClick={() => setSelected(s.subject)}
                      data-testid={`subject-tile-${s.subject}`}
                      className="nb-card p-6 text-left group"
                    >
                      <div className={`w-14 h-14 rounded-xl nb-border ${COLOR_BG[s.color] || "bg-white"} flex items-center justify-center mb-4`}>
                        <Icon className="w-7 h-7 text-black" strokeWidth={2.5} />
                      </div>
                      <h3 className="font-display text-2xl font-extrabold mb-1">{s.subject}</h3>
                      <p className="text-sm font-medium text-zinc-600 dark:text-zinc-400">
                        {s.exam_board} · {s.count} topics
                      </p>
                      <div className="mt-4 inline-flex items-center gap-1 text-sm font-bold">
                        Explore <ChevronRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                      </div>
                    </motion.button>
                  );
                })}
              </div>
            )}
          </>
        ) : (
          <>
            <button onClick={() => setSelected(null)} className="nb-button px-3 py-2 mb-6 inline-flex items-center gap-2 text-sm">
              <ArrowLeft className="w-4 h-4" /> All subjects
            </button>
            <div className="flex items-end justify-between gap-4 flex-wrap mb-6">
              <h1 className="font-display text-4xl sm:text-5xl font-black tracking-tighter">
                {selected}
              </h1>
              <button
                data-testid="start-mock-exam-btn"
                onClick={() => navigate(`/exam?subject=${encodeURIComponent(selected)}`)}
                className="nb-button px-4 py-3 bg-[#FFB38E] dark:bg-[#F6AD55] text-black flex items-center gap-2"
              >
                <Timer className="w-4 h-4" strokeWidth={3} /> Mock exam
              </button>
            </div>
            {loading ? (
              <div className="font-bold text-zinc-500">Loading…</div>
            ) : (
              <div className="space-y-3">
                {topics.map((t, i) => {
                  const mastery = t.progress?.mastery || 0;
                  return (
                    <motion.button
                      key={t.id}
                      initial={{ opacity: 0, x: -10 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: i * 0.03 }}
                      onClick={() => {
                        if (t.locked) navigate("/pricing");
                        else if (activeChildId) navigate(`/curriculum/topic/${t.id}`);
                      }}
                      disabled={!t.locked && !activeChildId}
                      data-testid={`topic-${t.id}`}
                      className={`nb-card w-full p-5 text-left flex items-center gap-4 ${t.locked ? "opacity-80" : ""}`}
                    >
                      <div className={`w-12 h-12 rounded-xl nb-border flex items-center justify-center font-display text-2xl font-black ${COLOR_BG[t.color] || "bg-white"} text-black`}>
                        {t.ordinal}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <div className="font-display text-xl font-extrabold truncate">{t.topic_name}</div>
                          {t.free && !user?.subscription_status && (
                            <span className="nb-border rounded-full px-2 py-0.5 bg-[#9DFFB0] text-black text-[10px] font-bold uppercase tracking-wider">Free</span>
                          )}
                        </div>
                        <div className="text-xs font-bold text-zinc-500 mt-0.5 line-clamp-1">{t.summary}</div>
                        {mastery > 0 && (
                          <div className="mt-2 h-1.5 bg-zinc-200 dark:bg-zinc-700 rounded-full overflow-hidden">
                            <div className="h-full bg-[#9DFFB0]" style={{ width: `${mastery}%` }} />
                          </div>
                        )}
                      </div>
                      {t.locked ? (
                        <div className="flex items-center gap-2">
                          <Lock className="w-4 h-4" />
                          <span className="hidden sm:inline label-caps text-zinc-500">Premium</span>
                        </div>
                      ) : (
                        <ChevronRight className="w-5 h-5" />
                      )}
                    </motion.button>
                  );
                })}
                {topics.some((t) => t.locked) && !user?.subscription_status && (
                  <div className="nb-card p-5 bg-[#D4C4FB] dark:bg-[#B794F4] text-black text-center mt-4">
                    <div className="font-display text-xl font-extrabold mb-1">Unlock the full {selected} curriculum</div>
                    <p className="text-sm font-medium mb-3">Premium gives your child every topic, every podcast, every quiz.</p>
                    <button onClick={() => navigate("/pricing")} data-testid="curr-upgrade-cta" className="nb-button px-5 py-2 bg-black text-white">
                      See plans
                    </button>
                  </div>
                )}
              </div>
            )}
            {!activeChildId && (
              <div className="nb-card p-5 bg-[#FFB38E] dark:bg-[#F6AD55] text-black text-center mt-6">
                <div className="font-bold">Pick a child profile first to track progress.</div>
                <button onClick={() => navigate("/family")} className="nb-button px-4 py-2 mt-2">Choose profile</button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
